import React, { useEffect, useRef, useState } from 'react';
import { X, Check } from 'lucide-react';
import { trackLead } from '../utils/fbPixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const EMPTY = { name: '', email: '', phone: '', weddingDate: '', message: '' };
const inputCls = 'w-full bg-transparent border-b border-white/25 focus:border-white py-2.5 text-sm text-white placeholder-white/35 outline-none transition-colors';

const validate = (f) => {
  const e = {};
  if (!f.name.trim()) e.name = 'Please share your name';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) e.email = 'Please enter a valid email';
  if (!f.phone.trim()) e.phone = 'Please add a phone number';
  return e;
};

const Field = ({ name, label, type = 'text', form, errors, onChange }) => (
  <div>
    {type === 'date' && <label className="block text-[10px] tracking-[0.2em] uppercase text-white/40">{label}</label>}
    <input type={type} name={name} style={type === 'date' ? { colorScheme: 'dark', color: form[name] ? '#fff' : 'rgba(255,255,255,0.45)' } : undefined} value={form[name]} onChange={onChange} placeholder={label} aria-label={label} className={inputCls} data-testid={`inquiry-popup-${name}`} />
    {errors[name] && <p className="text-[11px] text-red-300 mt-1" data-testid={`inquiry-popup-error-${name}`}>{errors[name]}</p>}
  </div>
);

/** Compact inquiry form in a popup — posts to the same endpoint as the Inquire page. */
export const InquiryPopup = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [state, setState] = useState('idle');

  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return undefined;
    setState((s) => (s === 'done' || s === 'error' ? 'idle' : s));
    setErrors({});
    const onKey = (e) => { if (e.key === 'Escape') closeRef.current(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;
  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) return;
    setState('sending');
    try {
      const res = await fetch(`${API_URL}/api/content/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: form.name, email: form.email, phone: form.phone,
          message: `Wedding Date: ${form.weddingDate || 'Not specified'}\nSource: Floating inquire popup\n\n${form.message}`,
        }),
      });
      if (!res.ok) throw new Error('failed');
      trackLead({ content_name: 'Wedding Inquiry', content_category: 'Popup Inquiry', value: 0, currency: 'CAD' });
      setState('done'); setForm(EMPTY);
    } catch (_) { setState('error'); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center sm:p-6" data-testid="inquiry-popup">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-black/50" onClick={onClose} data-testid="inquiry-popup-backdrop" />
      <div className="relative w-full sm:max-w-md text-white float-in max-h-[92vh] overflow-y-auto" role="dialog" aria-modal="true"
        style={{ background: 'rgba(0,0,0,0.86)', backdropFilter: 'blur(20px) saturate(140%)', WebkitBackdropFilter: 'blur(20px) saturate(140%)', border: '1px solid rgba(255,255,255,0.22)' }}>
        <div className="flex items-center justify-between px-6 pt-5 pb-3 border-b border-white/10">
          <p className="text-[10px] tracking-[0.25em] uppercase text-white/60">Inquire</p>
          <button type="button" onClick={onClose} aria-label="Close" className="w-8 h-8 flex items-center justify-center text-white/60 hover:text-white" data-testid="inquiry-popup-close"><X size={16} /></button>
        </div>
        {state === 'done' ? (
          <div className="px-6 py-10 text-center" data-testid="inquiry-popup-success">
            <Check size={22} className="mx-auto mb-4 text-white/80" />
            <p className="font-presti text-2xl mb-2">Thank you</p>
            <p className="text-white/55 text-sm">We&rsquo;ll be in touch within 24 hours.</p>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="px-6 py-6 space-y-5" data-testid="inquiry-popup-form">
            <p className="font-presti text-xl leading-snug">Tell us about your day</p>
            <Field name="name" label="Your names" form={form} errors={errors} onChange={onChange} />
            <Field name="email" label="Email" type="email" form={form} errors={errors} onChange={onChange} />
            <Field name="phone" label="Phone" type="tel" form={form} errors={errors} onChange={onChange} />
            <Field name="weddingDate" label="Wedding date" type="date" form={form} errors={errors} onChange={onChange} />
            <textarea name="message" value={form.message} onChange={onChange} rows={3} placeholder="Venue, events, anything we should know" aria-label="Message" className={`${inputCls} resize-none`} data-testid="inquiry-popup-message" />
            {state === 'error' && <p className="text-[12px] text-red-300" data-testid="inquiry-popup-submit-error">Something went wrong — please try again.</p>}
            <button type="submit" disabled={state === 'sending'} className="w-full px-4 py-3.5 bg-white text-black text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-white/90 transition-colors disabled:opacity-60" data-testid="inquiry-popup-submit">
              {state === 'sending' ? 'Sending…' : 'Send inquiry'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default InquiryPopup;
